import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { LessonStatus, SkillLesson } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

const STATUS_OPTIONS: Array<{ value: LessonStatus | 'all'; label: string }> = [
  { value: 'pending', label: '검토 대기' },
  { value: 'active', label: '활성' },
  { value: 'rejected', label: '기각' },
  { value: 'archived', label: '보관' },
  { value: 'all', label: '전체' },
];

const STATUS_LABEL: Record<LessonStatus, string> = {
  pending: '검토 대기',
  active: '활성',
  rejected: '기각',
  archived: '보관',
};

const STATUS_CLASS: Record<LessonStatus, string> = {
  pending: 'bg-[hsl(30_90%_50%)]/15 text-[hsl(30_90%_50%)]',
  active: 'bg-success/15 text-success',
  rejected: 'bg-destructive/15 text-destructive',
  archived: 'bg-surface-muted text-foreground-muted',
};

export function AdminSkillLessonsList() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<LessonStatus | 'all'>('pending');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'skill-lessons', filter],
    queryFn: () =>
      api.adminSkillLessonsList({
        limit: 50,
        ...(filter === 'all' ? {} : { status: filter }),
      }),
  });

  const lessons = data?.lessons ?? [];

  return (
    <PageShell
      header={
        <>
          <PageHeader
            title="경험에서 배운 점"
            count={lessons.length}
            description="Tool 호출 실패가 자동 수집됩니다. 검토 후 활성화하면 다음 세션부터 해당 Skill 의 system prompt 에 주입됩니다."
          />
          <div className="mt-4 flex items-center gap-2">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFilter(opt.value)}
                className={cn(
                  'px-3 h-8 rounded-full text-xs border',
                  filter === opt.value
                    ? 'bg-surface-muted border-border text-foreground'
                    : 'border-transparent text-foreground-muted hover:bg-surface-muted/60',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      }
    >
      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : '목록을 불러오지 못했습니다.'}
        </p>
      ) : (
        <div className="rounded-2xl border border-border bg-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted/40 text-xs uppercase tracking-wider text-foreground-muted">
              <tr>
                <Th>Skill / 제목</Th>
                <Th>Tool</Th>
                <Th align="right">실패 수</Th>
                <Th>최근 실패</Th>
                <Th>상태</Th>
              </tr>
            </thead>
            <tbody>
              {lessons.map((l) => (
                <Row
                  key={l.id}
                  lesson={l}
                  onOpen={() => navigate({ to: '/admin/skill-lessons/$id', params: { id: l.id } })}
                />
              ))}
              {lessons.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-foreground-muted py-10">
                    {filter === 'pending' ? '검토 대기 중인 실패가 없습니다.' : '항목이 없습니다.'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-2.5 font-medium', align === 'right' ? 'text-right' : 'text-left')}
    >
      {children}
    </th>
  );
}

function Row({ lesson, onOpen }: { lesson: SkillLesson; onOpen: () => void }) {
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: 행 전체 클릭으로 상세 모달 — 키보드 사용자는 행 안의 액션 버튼으로 접근
    <tr
      className="border-t border-border hover:bg-surface-muted/30 cursor-pointer"
      onClick={onOpen}
    >
      <td className="px-4 py-3">
        <div className="font-medium">{lesson.skillName}</div>
        <div className="mt-0.5 text-xs text-foreground-muted truncate max-w-[520px]">
          {lesson.title}
        </div>
      </td>
      <td className="px-4 py-3 font-mono text-xs text-foreground-muted">{lesson.toolKey}</td>
      <td className="px-4 py-3 text-right tabular-nums">{lesson.failureCount}</td>
      <td className="px-4 py-3 text-foreground-muted text-xs">
        {formatRelative(lesson.lastSeenAt)}
      </td>
      <td className="px-4 py-3">
        <span
          className={cn(
            'inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider',
            STATUS_CLASS[lesson.status],
          )}
        >
          {STATUS_LABEL[lesson.status]}
        </span>
      </td>
    </tr>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = now - then;
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec}초 전`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}분 전`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}일 전`;
  return new Date(iso).toLocaleDateString('ko-KR');
}
